import { pushThemeGtmEvent } from './theme-utils.ts';

export type ThemeMode = 'light' | 'dark' | 'system';

declare global {
  interface Window {
    afGetThemeMode?: () => ThemeMode;
    afSetThemeMode?: (mode: ThemeMode) => void;
    afToggleThemeMode?: () => ThemeMode;
    afCycleThemeMode?: () => ThemeMode;
  }
}

const THEME_STORAGE_KEY = 'loumars-theme-mode';

export function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return 'system';
  try {
    const val = localStorage.getItem(THEME_STORAGE_KEY);
    if (val === 'light' || val === 'dark' || val === 'system') return val;
  } catch {}
  return 'system';
}

export function updateThemeToggleUI(): void {
  if (typeof document === 'undefined') return;
  const isDark = document.documentElement.classList.contains('dark');
  const title = isDark ? 'Switch to Day Mode' : 'Switch to Night Mode';
  const iconName = isDark ? 'dark_mode' : 'light_mode';
  const toggles = document.querySelectorAll<HTMLElement>('[data-theme-toggle]');
  toggles.forEach(function (btn) {
    btn.setAttribute('role', 'switch');
    btn.setAttribute('aria-checked', isDark ? 'true' : 'false');
    btn.setAttribute('title', title);
    btn.setAttribute('aria-label', title);
    const iconEl = btn.querySelector<HTMLElement>('[data-theme-icon]');
    if (iconEl) {
      iconEl.textContent = iconName;
    }
    const thumb = btn.querySelector<HTMLElement>('[data-theme-switch-thumb]');
    if (thumb) {
      if (isDark) {
        thumb.classList.remove('translate-x-0');
        thumb.classList.add('translate-x-6');
      } else {
        thumb.classList.remove('translate-x-6');
        thumb.classList.add('translate-x-0');
      }
    }
  });
}

export function applyThemeMode(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  let isDark = false;

  if (mode === 'dark') {
    isDark = true;
  } else if (mode === 'light') {
    isDark = false;
  } else {
    try {
      const m =
        typeof window !== 'undefined' && window.matchMedia
          ? window.matchMedia('(prefers-color-scheme: dark)')
          : null;
      isDark = Boolean(m && m.matches);
    } catch {
      isDark = false;
    }
  }

  const targetClass = isDark ? 'dark' : 'light';
  const removeClass = isDark ? 'light' : 'dark';

  if (
    root.getAttribute('data-theme-mode') === mode &&
    root.classList.contains(targetClass) &&
    !root.classList.contains(removeClass)
  ) {
    updateThemeToggleUI();
    return;
  }

  if (isDark) {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.add('light');
    root.classList.remove('dark');
  }
  root.setAttribute('data-theme-mode', mode);

  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {}

  updateThemeToggleUI();

  try {
    window.dispatchEvent(
      new CustomEvent('loumars-theme-mode-changed', {
        detail: { mode: mode, isDark: isDark },
      })
    );
  } catch {}

  pushThemeGtmEvent('theme_change', {
    theme_mode: mode,
    is_dark: isDark,
  });
}

export function toggleThemeMode(): ThemeMode {
  if (typeof document === 'undefined') return 'light';
  const isDark = document.documentElement.classList.contains('dark');
  const next: ThemeMode = isDark ? 'light' : 'dark';
  applyThemeMode(next);
  return next;
}

export function cycleThemeMode(): ThemeMode {
  return toggleThemeMode();
}

export function bindThemeToggleListeners(container?: HTMLElement | Document): void {
  const root = container || document;
  if (!root) return;
  const toggles = root.querySelectorAll<HTMLElement>('[data-theme-toggle]');
  toggles.forEach(function (btn) {
    if (btn.getAttribute('data-theme-bound') === 'true' || btn.dataset.themeToggleBound) return;
    btn.setAttribute('data-theme-bound', 'true');
    btn.dataset.themeToggleBound = 'true';
    btn.addEventListener('click', function (e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (
        typeof window.afToggleThemeMode === 'function' &&
        window.afToggleThemeMode !== toggleThemeMode
      ) {
        window.afToggleThemeMode();
      } else if (
        typeof window.afCycleThemeMode === 'function' &&
        window.afCycleThemeMode !== cycleThemeMode
      ) {
        window.afCycleThemeMode();
      } else {
        toggleThemeMode();
      }
    });
  });
  if (!document.documentElement.getAttribute('data-theme-mode')) {
    applyThemeMode(getStoredThemeMode());
  }
}

export function initOsThemeListener(): void {
  if (typeof window !== 'undefined' && window.matchMedia) {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const osListener = function (): void {
        if (getStoredThemeMode() === 'system') {
          applyThemeMode('system');
        }
      };
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', osListener);
      } else if ((mediaQuery as unknown as { addListener: (cb: () => void) => void }).addListener) {
        (mediaQuery as unknown as { addListener: (cb: () => void) => void }).addListener(
          osListener
        );
      }
    } catch {}
  }
}

// Bind window globals if not already set
if (typeof window !== 'undefined') {
  if (typeof window.afGetThemeMode !== 'function') {
    window.afGetThemeMode = getStoredThemeMode;
  }
  if (typeof window.afSetThemeMode !== 'function') {
    window.afSetThemeMode = applyThemeMode;
  }
  if (typeof window.afToggleThemeMode !== 'function') {
    window.afToggleThemeMode = toggleThemeMode;
  }
  if (typeof window.afCycleThemeMode !== 'function') {
    window.afCycleThemeMode = cycleThemeMode;
  }
  initOsThemeListener();
}
